import { pluralSr, type Hype } from '@movienight/shared';
import { Face } from './Face';

/**
 * The hype meter (DESIGN.md §7).
 *
 * One flame per member, and the signal is honest because it is one vote —
 * there is no way to be more hyped than anybody else. What the row shows is
 * your own flame, the count, and the faces of everyone who lit theirs.
 *
 * The flame is drawn as pixels rather than pulled from an emoji font: an emoji
 * renders differently on every platform and lands somewhere between a cartoon
 * and a photograph, neither of which belongs next to a nine-slice marquee. Nine
 * rows of rects with `shape-rendering="crispEdges"` scale to any size without a
 * fractional pixel anywhere, which is the whole game with this art.
 */

/** A 7×9 flame. `x` is lit, `.` is nothing. */
const FLAME = [
  '...x...',
  '..xx...',
  '..xxx..',
  '.xxxx..',
  '.xxxxx.',
  'xxxxxxx',
  'xxxxxxx',
  'xxxxxxx',
  '.xxxxx.',
];

function Flame() {
  return (
    <svg
      className="hype__flame"
      viewBox="0 0 7 9"
      shapeRendering="crispEdges"
      aria-hidden="true"
      focusable="false"
    >
      {FLAME.flatMap((row, y) =>
        [...row].map((cell, x) =>
          cell === 'x' ? <rect key={`${x}-${y}`} x={x} y={y} width={1} height={1} /> : null,
        ),
      )}
    </svg>
  );
}

export function HypeMeter({ hype, onTap }: { hype: Hype; onTap: () => void }) {
  const label = hype.mine ? 'Ugasi svoj plamen' : 'Zapali plamen';

  return (
    <div className="hype">
      <button
        type="button"
        className={`hype__button${hype.mine ? ' is-lit' : ''}`}
        onClick={onTap}
        aria-pressed={hype.mine}
        aria-label={label}
        title={label}
      >
        <Flame />
      </button>

      <span className="hype__count">
        {hype.count > 0
          ? `${hype.count} ${pluralSr(hype.count, 'zainteresovan', 'zainteresovana', 'zainteresovanih')}`
          : 'Niko još'}
      </span>

      {hype.who.length > 0 && (
        <span className="hype__faces">
          {hype.who.map((member) => (
            <Face key={member.id} nickname={member.nickname} avatarUrl={member.avatarUrl} />
          ))}
        </span>
      )}
    </div>
  );
}
